import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import { APP_NAME } from "../config/brand";
import AppLogo from "../components/AppLogo";
import {
    alpha,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Link as MuiLink,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {
    Visibility,
    VisibilityOff,
    ArrowBack,
    AdminPanelSettingsOutlined,
    PersonOutlineOutlined,
} from "@mui/icons-material";
import { loginUserUnified } from "../redux/userRelated/userHandle";
import { underControl } from "../redux/userRelated/userSlice";
import Popup from "../components/Popup";
import ThemeToggle from "../components/ThemeToggle";
import { getDashboardPathForRole } from "../utils/authRoutes";
import imgClassCommunity from "../assets/landing/class-community.png";

const ROLE_TABS = [
    { label: "Staff", icon: AdminPanelSettingsOutlined, hint: "Directors, teachers, and platform operators — email and password" },
    { label: "Student", icon: PersonOutlineOutlined, hint: "Student ID, full name, and password" },
];

const UnifiedLogin = ({ schoolId: schoolIdProp }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const schoolId = schoolIdProp || searchParams.get("school") || "";

    const { status, currentUser, response, error, currentRole } = useSelector((state) => state.user);

    const [schoolName, setSchoolName] = useState("");
    const [tab, setTab] = useState(0);
    const [toggle, setToggle] = useState(false);
    const [loader, setLoader] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [rollNumberError, setRollNumberError] = useState(false);
    const [studentNameError, setStudentNameError] = useState(false);

    const isStaff = tab === 0;
    const backTo = schoolId ? `/school/${encodeURIComponent(schoolId)}` : "/";
    const isSchoolLogin = Boolean(schoolId);
    const prevStatusRef = useRef(status);

    // Allow opening /login from the landing page even when a session exists in localStorage.
    useEffect(() => {
        dispatch(underControl());
        prevStatusRef.current = "idle";
    }, [dispatch]);

    useEffect(() => {
        if (!schoolId) {
            setSchoolName("");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${getApiBaseUrl()}/SchoolPage/${schoolId}`);
                if (!cancelled && res.data?.schoolName) {
                    setSchoolName(res.data.schoolName);
                }
            } catch {
                if (!cancelled) setSchoolName("");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [schoolId]);

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!isStaff) {
            const rollNum = event.target.rollNumber.value;
            const studentName = event.target.studentName.value.trim();
            const password = event.target.password.value;

            if (!rollNum || !studentName || !password) {
                if (!rollNum) setRollNumberError(true);
                if (!studentName) setStudentNameError(true);
                if (!password) setPasswordError(true);
                return;
            }
            setLoader(true);
            dispatch(
                loginUserUnified({
                    accountType: "student",
                    rollNum,
                    studentName,
                    password,
                })
            );
            return;
        }

        const email = event.target.email.value;
        const password = event.target.password.value;

        if (!email || !password) {
            if (!email) setEmailError(true);
            if (!password) setPasswordError(true);
            return;
        }

        setLoader(true);
        dispatch(
            loginUserUnified({
                accountType: "staff",
                email,
                password,
            })
        );
    };

    const handleInputChange = (event) => {
        const { name } = event.target;
        if (name === "email") setEmailError(false);
        if (name === "password") setPasswordError(false);
        if (name === "rollNumber") setRollNumberError(false);
        if (name === "studentName") setStudentNameError(false);
    };

    useEffect(() => {
        const prevStatus = prevStatusRef.current;
        prevStatusRef.current = status;

        // Only redirect after a fresh login (loading → success), not when visiting /login with stale status.
        if (status === "success" && prevStatus === "loading" && currentUser) {
            const dashboard = getDashboardPathForRole(currentRole, currentUser);
            if (dashboard) {
                navigate(dashboard, { replace: true });
                dispatch(underControl());
            }
        } else if (status === "failed") {
            setMessage(response || "Login failed");
            setShowPopup(true);
            setLoader(false);
        } else if (status === "error") {
            setMessage(error?.response?.data?.message || error?.message || "Something went wrong");
            setShowPopup(true);
            setLoader(false);
        }
    }, [status, currentRole, navigate, error, response, currentUser, dispatch]);

    return (
        <>
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    bgcolor: "background.default",
                    overflow: "hidden",
                }}
            >
                {/* Visual panel */}
                <Box
                    sx={{
                        display: { xs: "none", md: "flex" },
                        flex: "1 1 55%",
                        position: "relative",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        p: 5,
                    }}
                >
                    <Box
                        component="img"
                        src={imgClassCommunity}
                        alt=""
                        sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(135deg, rgba(10,12,15,0.92) 0%, rgba(10,12,15,0.55) 50%, rgba(10,12,15,0.85) 100%)",
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundImage:
                                "linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)",
                            backgroundSize: "80px 80px",
                            pointerEvents: "none",
                        }}
                    />

                    <Box sx={{ position: "relative", zIndex: 1, maxWidth: 480 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 4 }}>
                            <AppLogo size={36} />
                            <Typography
                                sx={{
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontWeight: 700,
                                    fontSize: "1.05rem",
                                    color: "text.primary",
                                }}
                            >
                                {APP_NAME}
                            </Typography>
                        </Stack>

                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { md: "2.25rem", lg: "2.6rem" },
                                color: "text.primary",
                                mb: 2,
                                lineHeight: 1.15,
                            }}
                        >
                            {isSchoolLogin && schoolName
                                ? <>Welcome back to<br /><Box component="span" sx={{ color: "primary.main" }}>{schoolName}</Box></>
                                : <>Your school portal,<br /><Box component="span" sx={{ color: "primary.main" }}>one sign-in away.</Box></>}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: alpha("#f0ede8", 0.65), maxWidth: 380, lineHeight: 1.7 }}
                        >
                            Directors, teachers, platform staff, and students — one sign-in, routed to the right dashboard.
                        </Typography>
                    </Box>
                </Box>

                {/* Form panel */}
                <Box
                    sx={{
                        flex: { xs: "1 1 100%", md: "1 1 45%" },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        px: { xs: 3, sm: 5, md: 6 },
                        py: { xs: 4, md: 6 },
                        position: "relative",
                    }}
                >
                    {/* Mobile-only subtle bg glow */}
                    <Box
                        sx={{
                            display: { xs: "block", md: "none" },
                            position: "absolute",
                            top: "-10%",
                            right: "-20%",
                            width: 300,
                            height: 300,
                            borderRadius: "50%",
                            background: "radial-gradient(circle, rgba(232,201,126,0.08) 0%, transparent 70%)",
                            pointerEvents: "none",
                        }}
                    />

                    <Box sx={{ width: "100%", maxWidth: 420, position: "relative", zIndex: 1 }}>
                        <Box sx={{ position: "absolute", top: 0, right: 0 }}>
                            <ThemeToggle size="small" />
                        </Box>
                        <Button
                            component={Link}
                            to={backTo}
                            startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                            sx={{
                                alignSelf: "flex-start",
                                mb: 3,
                                color: "text.secondary",
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: 13,
                                px: 0,
                                "&:hover": { color: "text.primary", bgcolor: "transparent" },
                            }}
                        >
                            {isSchoolLogin ? "Back to school page" : "Back to home"}
                        </Button>

                        {/* Mobile logo */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{ mb: 3, display: { xs: "flex", md: "none" } }}
                        >
                            <AppLogo size={32} />
                            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, color: "text.primary" }}>
                                {APP_NAME}
                            </Typography>
                        </Stack>

                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: "1.75rem", sm: "2rem" },
                                color: "text.primary",
                                mb: 0.75,
                            }}
                        >
                            {isSchoolLogin && schoolName ? `Sign in — ${schoolName}` : "Sign in"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {isSchoolLogin
                                ? "Choose Staff or Student — we'll send you to the right portal."
                                : "Staff and students share one page — your credentials decide where you land."}
                        </Typography>

                        {/* Role toggle */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                p: 0.75,
                                mb: 2.5,
                                borderRadius: "4px",
                                bgcolor: alpha("#fff", 0.04),
                                border: "1px solid rgba(255,255,255,0.07)",
                            }}
                        >
                            {ROLE_TABS.map(({ label, icon: Icon }, i) => (
                                <Button
                                    key={label}
                                    fullWidth
                                    onClick={() => setTab(i)}
                                    startIcon={<Icon sx={{ fontSize: 18 }} />}
                                    sx={{
                                        py: 1.25,
                                        fontFamily: '"DM Sans", sans-serif',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        color: tab === i ? "#0a0c0f" : "text.secondary",
                                        bgcolor: tab === i ? "primary.main" : "transparent",
                                        borderRadius: "3px",
                                        boxShadow: tab === i ? "0 2px 12px rgba(232,201,126,0.25)" : "none",
                                        "&:hover": {
                                            bgcolor: tab === i ? "primary.light" : alpha("#fff", 0.04),
                                        },
                                    }}
                                >
                                    {label}
                                </Button>
                            ))}
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2.5 }}>
                            {ROLE_TABS[tab]?.hint}
                        </Typography>

                        <Box component="form" noValidate onSubmit={handleSubmit}>
                            {isStaff ? (
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="email"
                                    label="Email"
                                    name="email"
                                    autoComplete="email"
                                    autoFocus
                                    error={emailError}
                                    helperText={
                                        emailError
                                            ? "Email is required"
                                            : "School directors, teachers, and platform operators all sign in here"
                                    }
                                    onChange={handleInputChange}
                                    sx={{ mt: 0 }}
                                />
                            ) : (
                                <>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="rollNumber"
                                        label="Student ID"
                                        name="rollNumber"
                                        autoComplete="off"
                                        type="number"
                                        autoFocus
                                        error={rollNumberError}
                                        helperText={rollNumberError && "Student ID is required"}
                                        onChange={handleInputChange}
                                        sx={{ mt: 0 }}
                                    />
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="studentName"
                                        label="Full name (as registered)"
                                        name="studentName"
                                        autoComplete="name"
                                        error={studentNameError}
                                        helperText={studentNameError && "Name is required"}
                                        onChange={handleInputChange}
                                    />
                                </>
                            )}
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type={toggle ? "text" : "password"}
                                id="password"
                                autoComplete="current-password"
                                error={passwordError}
                                helperText={passwordError && "Password is required"}
                                onChange={handleInputChange}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setToggle(!toggle)}
                                                edge="end"
                                                sx={{ color: "text.secondary" }}
                                            >
                                                {toggle ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                                <MuiLink
                                    component={Link}
                                    to={isStaff ? "/forgot-password" : "/forgot-password?type=student"}
                                    sx={{
                                        color: "primary.main",
                                        fontWeight: 600,
                                        fontSize: 13,
                                        textDecoration: "none",
                                        "&:hover": { textDecoration: "underline" },
                                    }}
                                >
                                    Forgot password?
                                </MuiLink>
                            </Box>

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="large"
                                disabled={loader}
                                sx={{ mt: 3, py: 1.4 }}
                            >
                                {loader ? <CircularProgress size={24} sx={{ color: "#0a0c0f" }} /> : "Sign in"}
                            </Button>

                            {!isSchoolLogin && isStaff ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 3, textAlign: "center", fontFamily: '"DM Sans", sans-serif' }}
                                >
                                    New school?{" "}
                                    <MuiLink
                                        component={Link}
                                        to="/Adminregister"
                                        sx={{
                                            color: "primary.main",
                                            fontWeight: 600,
                                            textDecoration: "none",
                                            "&:hover": { textDecoration: "underline" },
                                        }}
                                    >
                                        Register admin account
                                    </MuiLink>
                                </Typography>
                            ) : isSchoolLogin ? (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    textAlign="center"
                                    sx={{ mt: 3 }}
                                >
                                    Directors and teachers use <strong>Staff</strong>. Students use{" "}
                                    <strong>Student</strong> with roll number and registered name.
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </>
    );
};

export default UnifiedLogin;
